/**
 * UltraPass readiness: is the app installed, and is the user set up to use it?
 *
 * Two separate questions, with very different answers.
 *
 * **Is UltraPass selected as the credential provider?** Undetectable. Not from the web, and not
 * even from a native app that isn't UltraPass itself. iOS exposes no API for it at all; Android's
 * `CredentialManager.isEnabledCredentialProviderService()` rejects any caller whose package isn't
 * the provider's own. There is no clever workaround here — the only ground truth is the ceremony
 * outcome, which is why {@link classifyReadinessFailure} exists.
 *
 * **Is UltraPass installed?** Answerable on Chromium/Android via
 * `navigator.getInstalledRelatedApps()`, and nowhere else. On iOS the honest answer is "unknown",
 * and the right move is to let Safari answer it: a Smart App Banner meta tag makes Safari itself
 * render OPEN vs GET. Safari knows; script does not. See {@link getSmartAppBannerContent}.
 *
 * Note the asymmetry that follows: on iOS the *user* can be shown an accurate install state that
 * the *page* is never told, so nothing here should gate behaviour on knowing it.
 *
 * Probing the `ultrapass://` scheme is deliberately not offered. On iOS Safari a scheme with no
 * handler produces a visible "cannot open the page" dialog, so the probe costs the user an error
 * modal to learn something it then cannot report reliably anyway.
 *
 * Because installation is only partly knowable and selection is not knowable at all, guidance is
 * unconditional on mobile rather than gated on a check. {@link getSetupGuidance} returns copy
 * ported from the native apps so the web instructions match what users are told in-app.
 *
 * @module core/readiness
 */

import { Platform } from '../utils/platform';

/**
 * How confident we are that UltraPass is present on the device.
 *
 * There is no `not-configured`: whether the user selected UltraPass in their passkey settings is
 * not observable, so it is never a state this module reports.
 */
export type InstallStatus =
  /** Positively confirmed present. Only ever produced by a Digital Asset Links match. */
  | 'installed'
  /** Positively confirmed absent. Only ever produced by a Digital Asset Links match. */
  | 'not-installed'
  /** No mechanism available on this platform/browser. The default, and not a failure. */
  | 'unknown';

/**
 * Result of an installation check, including why it reached that answer.
 */
export interface InstallCheckResult {
  /** What we could determine. */
  status: InstallStatus;

  /**
   * Which mechanism produced {@link status}.
   *
   * - `related-apps` — `navigator.getInstalledRelatedApps()` returned an answer.
   * - `unsupported` — the API does not exist in this browser (everything but Chromium/Android).
   * - `not-configured` — the API exists but no `androidPackageName` was supplied to check for.
   * - `error` — the API threw; see {@link InstallCheckResult.detail}.
   */
  method: 'related-apps' | 'unsupported' | 'not-configured' | 'error';

  /** Human-readable note for logs. Never shown to users. */
  detail?: string;
}

/**
 * A single numbered step in the provider setup instructions.
 */
export interface SetupStep {
  /** 1-based position. */
  number: number;

  /**
   * Step text. May contain `**bold**` spans, matching the native strings this is ported from
   * (`autofill.step.*`), which mark up the literal Settings labels the user is looking for.
   */
  text: string;
}

/**
 * Platform-specific copy telling a user how to make UltraPass usable.
 *
 * Ported from the native apps so a user who has seen the in-app screen reads the same words here.
 * Every field is overridable through {@link ReadinessCopyOverrides}.
 */
export interface SetupGuidance {
  /** Platform this guidance targets. */
  platform: Platform;

  /** Short heading. iOS: `autofill.title`. */
  title: string;

  /** One-line statement of what is required. iOS: `autofill.requirement`. */
  requirement: string;

  /** Longer explanation of why. iOS: `autofill.alertBody`. */
  body: string;

  /** Ordered Settings steps. iOS: `autofill.step.*`. */
  steps: SetupStep[];

  /** Reassurance that the flow resumes on return. iOS: `autofill.returnHint`. */
  returnHint: string;

  /** Label for the "get the app" action. */
  installLabel: string;

  /** What the user is told when they picked the wrong provider mid-ceremony. */
  wrongProviderMessage: string;

  /** Store URL for this platform, if one is configured. */
  storeUrl?: string;
}

/**
 * Per-platform copy overrides, merged field-by-field over the defaults.
 *
 * Every string an integrator might need to reword or localize is reachable from here — nothing in
 * this module hardcodes user-visible text that cannot be replaced.
 */
export type ReadinessCopyOverrides = {
  [P in 'iOS' | 'Android' | 'Desktop' | 'Unknown']?: Partial<Omit<SetupGuidance, 'platform'>>;
};

/**
 * Inputs for the readiness helpers.
 *
 * Deliberately not the full `SDKConfig`: these helpers are useful on a landing page before an SDK
 * instance exists, which is exactly when the guidance matters most.
 */
export interface ReadinessOptions {
  /**
   * Android package name to look for, and the subject of the Digital Asset Links association.
   *
   * @default 'com.privateid.ultrapass'
   */
  androidPackageName?: string;

  /**
   * Numeric App Store ID for the iOS Smart App Banner — digits only, no `id` prefix.
   *
   * Defaults to {@link ULTRAPASS_APP_STORE_ID}. Pass `''` to suppress the banner entirely.
   */
  appleItunesAppId?: string;

  /** Store URLs, keyed by platform. */
  appStoreUrls?: {
    iOS?: string;
    Android?: string;
  };

  /** Copy overrides, merged over {@link DEFAULT_READINESS_COPY}. */
  copy?: ReadinessCopyOverrides;
}

/**
 * Default Android package name for UltraPass.
 *
 * Matches `ProviderChecker.providerPackage` in the Android launcher.
 */
export const DEFAULT_ANDROID_PACKAGE = 'com.privateid.ultrapass';

/**
 * Numeric App Store ID for Ultrapass, verified 2026-08-13 against the live listing
 * (`apps.apple.com/app/ultrapass/id6758148839`, seller "PRIVATE IDENTITY LLC").
 *
 * Note this is the **production** listing. Staging UltraPass is a separate app distributed
 * outside the App Store, so a staging-configured page should pass `appleItunesAppId: ''` rather
 * than send testers to install the production build alongside it.
 */
export const ULTRAPASS_APP_STORE_ID = '6758148839';

/**
 * Public store URLs for production UltraPass.
 */
export const ULTRAPASS_STORE_URLS = {
  iOS: `https://apps.apple.com/app/ultrapass/id${ULTRAPASS_APP_STORE_ID}`,
  Android: `https://play.google.com/store/apps/details?id=${DEFAULT_ANDROID_PACKAGE}`
} as const;

/**
 * Default setup copy, ported from the native apps.
 *
 * iOS strings come from `Localizable.xcstrings` (`autofill.*`), rendered natively by
 * `EnableAutoFillView.swift`. Android has no equivalent step-by-step screen, so its steps are
 * written to match the Android Settings labels, while its wrong-provider line is taken verbatim
 * from `demo_msg_wrong_provider` in the Android client demo.
 *
 * Kept as a plain exported constant so integrators can diff their overrides against it.
 */
export const DEFAULT_READINESS_COPY: Record<
  'iOS' | 'Android' | 'Desktop' | 'Unknown',
  Omit<SetupGuidance, 'platform' | 'storeUrl'>
> = {
  iOS: {
    title: 'Enable Ultrapass AutoFill',
    requirement: 'Passkey authentication requires Ultrapass to be enabled in your device Settings.',
    body:
      'To complete this action, Ultrapass needs to be set up as an AutoFill provider on your ' +
      'device.\n\nThis allows Ultrapass to securely provide passkeys when websites and apps ' +
      'request authentication.',
    steps: [
      { number: 1, text: 'Open **Settings** on your device' },
      { number: 2, text: 'Go to **General**' },
      { number: 3, text: 'Tap **AutoFill and Passwords**' },
      { number: 4, text: 'Enable **Ultrapass**' }
    ],
    returnHint: 'After enabling, return to this page. The process will continue automatically.',
    installLabel: 'Get UltraPass',
    wrongProviderMessage:
      'Wrong passkey provider selected. Please select UltraPass from the passkey provider list.'
  },
  Android: {
    title: 'Enable UltraPass as your passkey provider',
    requirement:
      'Passkey authentication requires UltraPass to be enabled in Settings > Passkeys & Passwords.',
    body:
      'To complete this action, UltraPass needs to be enabled as a passkey provider on your ' +
      'device.\n\nThis allows UltraPass to securely provide passkeys when websites and apps ' +
      'request authentication.',
    steps: [
      { number: 1, text: 'Open **Settings** on your device' },
      { number: 2, text: 'Go to **Passwords & accounts**' },
      { number: 3, text: 'Tap **Passkeys, passwords & data services**' },
      { number: 4, text: 'Enable **UltraPass**' }
    ],
    returnHint: 'After enabling, return to this page. The process will continue automatically.',
    installLabel: 'Get UltraPass on Google Play',
    wrongProviderMessage:
      'Wrong passkey provider selected. Please select UltraPass from the passkey provider list.'
  },
  Desktop: {
    title: 'Use UltraPass on this computer',
    requirement:
      'Sign in with UltraPass on the desktop by scanning the QR code your browser shows, or by ' +
      'using an UltraPass security key.',
    body:
      'Desktop browsers reach UltraPass through the operating system, either over the cross-device ' +
      'QR flow or through a connected UltraPass device.',
    steps: [],
    returnHint: '',
    installLabel: 'Get UltraPass',
    wrongProviderMessage:
      'Wrong passkey provider selected. Please select UltraPass from the passkey provider list.'
  },
  Unknown: {
    title: 'UltraPass required',
    requirement: 'This sign-in needs the UltraPass app and it enabled as your passkey provider.',
    body: 'Install UltraPass and enable it as a passkey provider to continue.',
    steps: [],
    returnHint: '',
    installLabel: 'Get UltraPass',
    wrongProviderMessage:
      'Wrong passkey provider selected. Please select UltraPass from the passkey provider list.'
  }
};

/**
 * Shape of `navigator.getInstalledRelatedApps()`, which TypeScript's DOM lib does not declare.
 */
interface RelatedApplication {
  platform: string;
  id?: string;
  url?: string;
  version?: string;
}

/**
 * Checks whether UltraPass is installed.
 *
 * Only Chromium on Android can answer. Everywhere else this resolves to `unknown`, which callers
 * should treat as "show the guidance anyway" rather than as an error — the whole point of this
 * module is that unconditional guidance is the fallback.
 *
 * Even on Android, a positive result means only that the app is present. It says nothing about
 * whether the user enabled it as a credential provider, which is not observable.
 *
 * Three things must all be true for a real answer on Android:
 *
 * 1. The page serves a web app manifest whose `related_applications` lists the Play package, with
 *    `prefer_related_applications` present.
 * 2. The origin serves `/.well-known/assetlinks.json` naming that package and its SHA-256 signing
 *    fingerprint — obtainable only from whoever signs the UltraPass release.
 * 3. The page is on HTTPS.
 *
 * Miss any of them and the call returns an empty list, which is indistinguishable from "not
 * installed". This function therefore reports `not-installed` on an empty list only when a package
 * name was supplied; treat that verdict as advisory and never as a hard block.
 *
 * @param options - Package name to look for
 * @returns What could be determined, and by what means. Never rejects.
 *
 * @example
 * ```typescript
 * const { status } = await checkUltraPassInstalled();
 * if (status === 'not-installed') {
 *   showInstallPrompt();
 * }
 * ```
 */
export async function checkUltraPassInstalled(
  options: ReadinessOptions = {}
): Promise<InstallCheckResult> {
  const packageName = options.androidPackageName || DEFAULT_ANDROID_PACKAGE;

  if (typeof navigator === 'undefined') {
    return { status: 'unknown', method: 'unsupported', detail: 'No navigator (non-browser)' };
  }

  const getInstalledRelatedApps = (
    navigator as Navigator & {
      getInstalledRelatedApps?: () => Promise<RelatedApplication[]>;
    }
  ).getInstalledRelatedApps;

  if (typeof getInstalledRelatedApps !== 'function') {
    return {
      status: 'unknown',
      method: 'unsupported',
      detail: 'navigator.getInstalledRelatedApps is unavailable (Chromium/Android only)'
    };
  }

  if (!packageName) {
    return { status: 'unknown', method: 'not-configured', detail: 'No androidPackageName' };
  }

  try {
    const apps = await getInstalledRelatedApps.call(navigator);
    const match = apps.find((app) => app.platform === 'play' && app.id === packageName);

    if (match) {
      return {
        status: 'installed',
        method: 'related-apps',
        detail: `Matched ${packageName}${match.version ? ` v${match.version}` : ''}`
      };
    }

    return {
      status: 'not-installed',
      method: 'related-apps',
      detail:
        apps.length === 0
          ? 'No related apps reported — may also mean the manifest or assetlinks.json is missing'
          : `Related apps reported, none matching ${packageName}`
    };
  } catch (error) {
    return {
      status: 'unknown',
      method: 'error',
      detail: error instanceof Error ? error.message : String(error)
    };
  }
}

/**
 * Builds the `content` value for an iOS Smart App Banner meta tag.
 *
 * This is the only way a web page gets an install affordance whose state is actually correct on
 * iOS. Safari resolves the app ID against what is installed and labels the banner OPEN or GET
 * itself. Script never learns which — but the user sees the right button, which is the part that
 * matters.
 *
 * Safari on iOS only. Chrome and Firefox on iOS ignore the tag, and so does every desktop browser.
 *
 * @param options - Must include `appleItunesAppId`
 * @param extras - Optional `app-argument` deep link, and affiliate parameters
 * @returns The `content` string, or `null` if no app ID was configured
 *
 * @example
 * ```typescript
 * const content = getSmartAppBannerContent({ appleItunesAppId: '1234567890' });
 * if (content) {
 *   const meta = document.createElement('meta');
 *   meta.name = 'apple-itunes-app';
 *   meta.content = content;
 *   document.head.appendChild(meta);
 * }
 * ```
 */
export function getSmartAppBannerContent(
  options: ReadinessOptions = {},
  extras: { appArgument?: string; affiliateData?: string } = {}
): string | null {
  // `?? default` rather than `|| default`: an explicit '' is how a caller suppresses the banner
  // (staging pages, where the production listing is the wrong app to advertise).
  const appId = (options.appleItunesAppId ?? ULTRAPASS_APP_STORE_ID).trim();

  if (!appId) {
    return null;
  }

  const parts = [`app-id=${appId}`];

  if (extras.affiliateData) {
    parts.push(`affiliate-data=${extras.affiliateData}`);
  }

  if (extras.appArgument) {
    parts.push(`app-argument=${extras.appArgument}`);
  }

  return parts.join(', ');
}

/**
 * Installs the iOS Smart App Banner meta tag into the document head.
 *
 * Idempotent: an existing `apple-itunes-app` tag is updated rather than duplicated. Safari only
 * honours the tag on page load, so call this before first paint — adding it later has no effect
 * until the next navigation.
 *
 * @param options - Must include `appleItunesAppId`
 * @param extras - Passed through to {@link getSmartAppBannerContent}
 * @returns `true` if a tag was written
 */
export function installSmartAppBanner(
  options: ReadinessOptions = {},
  extras: { appArgument?: string; affiliateData?: string } = {}
): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const content = getSmartAppBannerContent(options, extras);
  if (!content) {
    return false;
  }

  let meta = document.querySelector<HTMLMetaElement>('meta[name="apple-itunes-app"]');

  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    document.head.appendChild(meta);
  }

  meta.content = content;
  return true;
}

/**
 * Returns the setup guidance to show for a platform.
 *
 * Not conditional on any check, by design — whether the user selected UltraPass as their provider
 * cannot be observed, so guidance that waits for a negative signal would never appear.
 *
 * @param platform - Target platform, usually from `detectPlatform()`
 * @param options - Store URLs and copy overrides
 * @returns Fully resolved copy, defaults merged with any overrides
 *
 * @example
 * ```typescript
 * const guidance = getSetupGuidance(detectPlatform(), {
 *   appStoreUrls: { iOS: 'https://apps.apple.com/app/ultrapass/id1234567890' }
 * });
 * heading.textContent = guidance.title;
 * ```
 */
export function getSetupGuidance(
  platform: Platform,
  options: ReadinessOptions = {}
): SetupGuidance {
  const base = DEFAULT_READINESS_COPY[platform] || DEFAULT_READINESS_COPY.Unknown;
  const override = options.copy?.[platform] || {};

  const storeUrl =
    platform === 'iOS'
      ? (options.appStoreUrls?.iOS ?? ULTRAPASS_STORE_URLS.iOS)
      : platform === 'Android'
        ? (options.appStoreUrls?.Android ?? ULTRAPASS_STORE_URLS.Android)
        : undefined;

  return {
    platform,
    title: override.title ?? base.title,
    requirement: override.requirement ?? base.requirement,
    body: override.body ?? base.body,
    steps: override.steps ?? base.steps,
    returnHint: override.returnHint ?? base.returnHint,
    installLabel: override.installLabel ?? base.installLabel,
    wrongProviderMessage: override.wrongProviderMessage ?? base.wrongProviderMessage,
    storeUrl: override.storeUrl ?? storeUrl
  };
}

/**
 * Why a ceremony looks like it failed for readiness reasons rather than for real ones.
 */
export type ReadinessFailureKind =
  /** No provider offered a credential. Usually UltraPass is absent or not enabled in Settings. */
  | 'provider-unavailable'
  /** A credential came back, but from some other authenticator. The user picked the wrong one. */
  | 'wrong-provider'
  /** The failure does not look readiness-related; report it as itself. */
  | 'unrelated';

/**
 * A ceremony failure classified against the readiness cases, with copy to show.
 */
export interface ReadinessFailure {
  kind: ReadinessFailureKind;

  /** Message to show the user. Empty for `unrelated` — the caller's own error text applies. */
  message: string;

  /** Whether showing the full setup steps is warranted. */
  shouldShowGuidance: boolean;
}

/**
 * Classifies a ceremony error as a readiness problem or a genuine one.
 *
 * This is where the "user never selected UltraPass" case is finally caught. It cannot be detected
 * up front, so it surfaces here — as a `NotAllowedError`, or as an AAGUID mismatch when some other
 * provider answered instead.
 *
 * `NotAllowedError` is inherently ambiguous: the WebAuthn spec has the browser report a plain user
 * cancellation and an unsatisfiable request identically, precisely so that pages cannot probe for
 * what a user has installed. This maps it to `provider-unavailable` because a user who cancelled
 * loses nothing by seeing the setup steps, while a user with no working provider is stuck without
 * them.
 *
 * @param error - The thrown error
 * @param platform - Platform whose copy to use
 * @param options - Copy overrides
 * @returns Classification plus the message to show
 *
 * @example
 * ```typescript
 * try {
 *   await sdk.register({ username });
 * } catch (error) {
 *   const failure = classifyReadinessFailure(error, detectPlatform());
 *   if (failure.shouldShowGuidance) showSetupPanel(failure.message);
 *   else showError(error);
 * }
 * ```
 */
export function classifyReadinessFailure(
  error: unknown,
  platform: Platform,
  options: ReadinessOptions = {}
): ReadinessFailure {
  const guidance = getSetupGuidance(platform, options);
  const name = (error as { name?: string } | null)?.name || '';
  const code = (error as { code?: string } | null)?.code || '';
  const rawMessage = (error as { message?: string } | null)?.message || '';
  const message = rawMessage.toLowerCase();

  // An AAGUID mismatch means some authenticator did respond — it just was not UltraPass. That is
  // the wrong-provider case exactly, and the only one we can identify positively.
  const isAaguidMismatch =
    code === 'AAGUID_MISMATCH' ||
    name === 'VerificationError' ||
    message.includes('aaguid');

  if (isAaguidMismatch) {
    return {
      kind: 'wrong-provider',
      message: guidance.wrongProviderMessage,
      shouldShowGuidance: true
    };
  }

  // Android Credential Manager surfaces a missing/disabled provider through these; the strings
  // match ProviderChecker.isProviderNotEnabledError() in the Android launcher.
  const isNoProvider =
    name === 'NotAllowedError' ||
    message.includes('no create options available') ||
    message.includes('no credential options available') ||
    message.includes('nocreateoption') ||
    message.includes('nocredential');

  if (isNoProvider) {
    return {
      kind: 'provider-unavailable',
      message: guidance.requirement,
      shouldShowGuidance: true
    };
  }

  return { kind: 'unrelated', message: '', shouldShowGuidance: false };
}
